import type { Dict } from "../types";

export const inventory: Dict = {
  en: {

    // Inventory toast messages (errorToast / successToast wrapper)
    "Inventory.toast.loadCategoriesFailed": "Could not load categories",
    "Inventory.toast.loadSuppliersFailed": "Could not load suppliers",
    "Inventory.toast.loadLocationsFailed": "Could not load locations",
    "Inventory.toast.noLocationForStock": "Create a location before adding initial stock.",
    "Inventory.toast.loadSkusFailed": "Could not load SKUs",
    "Inventory.toast.loadDataFailed": "Could not load inventory data",
    "Inventory.toast.loadMovementsFailed": "Could not load movement history",
    "Inventory.toast.invalidPhoto": "Unsupported image format",
    "Inventory.toast.saveProductFailed": "Could not save product",
    "Inventory.toast.oldPhotoNotRemoved": "New photo saved, but the previous file could not be deleted",
    "Inventory.toast.photoNotSaved": "Product saved, but the photo could not be updated",
    "Inventory.toast.saveSupplierFailed": "Could not save supplier",
    "Inventory.toast.saveCategoryFailed": "Could not save category",
    "Inventory.toast.saveLocationFailed": "Could not save location",
    "Inventory.toast.saveMovementFailed": "Could not record movement",
    "Inventory.toast.deleteProductFailed": "Could not delete product",
    "Inventory.toast.deleteSupplierFailed": "Could not delete supplier",
    "Inventory.toast.deleteCategoryFailed": "Could not delete category",
    "Inventory.toast.deleteLocationFailed": "Could not delete location",
    "Inventory.toast.bulkDeleteFailed": "Could not delete selected products",
    "Inventory.toast.archiveProductFailed": "Could not archive product",
    "Inventory.toast.restoreProductFailed": "Could not restore product",
    "Inventory.headerSubtitle": "Products, stock, movements and suppliers.",
    "Inventory.tabProducts": "Products",
    "Inventory.tileProducts": "Products",
    "Inventory.tileLowStock": "Low stock",
    "Inventory.tileOutOfStock": "Out of stock",
    "Inventory.tileStockValue": "Stock value",

    // Inventory
    "Inventory.title": "Inventory",
    "Inventory.lowStock": "Low Stock",
    "Inventory.outOfStock": "Out of Stock",
    "Inventory.inStock": "In Stock",
    "Inventory.totalValue": "Total Value",
    "Inventory.upgradeRequired": "Inventory Module Unavailable",
    "Inventory.upgradeDescription":
      "The inventory module is not available on your current plan. Please upgrade to the PRO or ENTERPRISE plan to access this feature.",
    "Inventory.contactUs": "Contact Us to Upgrade",
    "Inventory.noAccessTitle": "No access",
    "Inventory.noAccessDescription": "You do not have permission to view inventory. Contact the salon owner to request access.",
    "Inventory.searchPlaceholder": "Search products...",
    "Inventory.filterCategory": "Filter by category",
    "Inventory.allCategories": "All Categories",
    "Inventory.allStockLevels": "All Stock Levels",
    "Inventory.allLevels": "All Levels",
    "Inventory.stockLevel": "Stock Level",
    "Inventory.newProduct": "Product",
    "Inventory.newCategory": "Category",
    "Inventory.newSupplier": "Supplier",
    "Inventory.newLocation": "Location",
    "Inventory.downloadReport": "Download Report",
    "Inventory.reportTitle": "Download Inventory Report",
    "Inventory.startDate": "Start Date",
    "Inventory.endDate": "End Date",
    "Inventory.selectStartDate": "Select a date",
    "Inventory.selectEndDate": "Select a date",
    "Inventory.reportDescription":
      "The report will include all stock movements (Purchases, Sales, Returns, Waste and Adjustments) for the selected period, with a summary table and detailed chronological list.",
    "Inventory.reportGenerating": "Generating...",
    "Inventory.downloadPDF": "Download PDF",
    "Inventory.reportGenerated": "Report generated",
    "Inventory.reportDownloaded": "The inventory report has been downloaded successfully",
    "Inventory.selectPeriod": "Please select a period",
    "Inventory.startBeforeEnd": "The start date must be before the end date",
    "Inventory.errorGeneratingReport": "Error generating report",
    "Inventory.manageCategories": "Manage Categories",
    "Inventory.manageSuppliers": "Manage Suppliers",
    "Inventory.productName": "Product Name",
    "Inventory.category": "Category",
    "Inventory.supplier": "Supplier",
    "Inventory.stock": "Stock",
    "Inventory.cost": "Cost",
    "Inventory.retail": "Retail",
    "Inventory.actions": "Actions",
    "Inventory.filterByCategory": "Filter by Category",
    "Inventory.filterBySupplier": "Filter by Supplier",
    "Inventory.filterByStatus": "Filter by Status",
    "Inventory.loading": "Loading inventory...",
    "Inventory.noProducts": "No products found",
    "Inventory.createProduct": "Create Product",
    "Inventory.editProduct": "Edit Product",
    "Inventory.addProduct": "Add a new product to inventory",
    "Inventory.updateProduct": "Update product details",
    "Inventory.productPhoto": "Product Photo",
    "Inventory.photoHint": "JPEG, PNG or WebP. Max 800x800px.",
    "Inventory.removePhoto": "Remove photo",
    "Inventory.selected": "selected",
    "Inventory.printLabels": "Print Labels",
    "Inventory.labelPrintTitle": "Print Product Labels",
    "Inventory.productsSelected": "products selected",
    "Inventory.labelSize": "Label Size",
    "Inventory.customSize": "Custom Size",
    "Inventory.labelWidth": "Width (inches)",
    "Inventory.labelHeight": "Height (inches)",
    "Inventory.quantityPerProduct": "Qty per product",
    "Inventory.preview": "Preview",
    "Inventory.print": "Print",
    "Inventory.locations": "Locations",
    "Inventory.manageLocations": "Manage Locations",
    "Inventory.manageLocationsDescription": "Create and manage your salon's storage locations",
    "Inventory.locationName": "Location Name",
    "Inventory.locationNamePlaceholder": "e.g., Display, Backstore",
    "Inventory.locationColor": "Color",
    "Inventory.addLocation": "Add Location",
    "Inventory.editLocation": "Edit Location",
    "Inventory.locationCreated": "Location created",
    "Inventory.locationUpdated": "Location updated",
    "Inventory.confirmDeleteLocation": "Are you sure you want to delete this location?",
    "Inventory.deleteLocation": "Delete location",
    "Inventory.locationDeleted": "Location deleted",
    "Inventory.existingLocations": "Existing Locations",
    "Inventory.noLocations": "No locations yet",
    "Inventory.locationNameRequired": "Location name is required",
    "Inventory.maxLocationsReached": "Maximum 5 locations per salon",
    "Inventory.cannotDeleteLastLocation": "Cannot delete the last location",
    "Inventory.cannotDeleteLocationWithStock": "Cannot delete a location that has stock. Transfer stock first.",
    "Inventory.cannotVerifyLocationStock": "The stock of this location could not be checked. Close this window and try again.",
    "Inventory.transfer": "Transfer",
    "Inventory.transferStock": "Transfer between locations",
    "Inventory.sourceLocation": "Source Location",
    "Inventory.destinationLocation": "Destination Location",
    "Inventory.selectLocation": "Select location",
    "Inventory.deductFromLocation": "Deduct from",
    "Inventory.stockByLocation": "Stock by location",
    "Inventory.locationRequired": "Please select a location",
    "Inventory.destinationRequired": "Please select a destination location",
    "Inventory.cannotTransferSameLocation": "Source and destination must be different",
    "Inventory.insufficientLocationStock": "Insufficient stock at this location",
    "Inventory.initialStockLocation": "Location for initial stock",
    "Inventory.totalProducts": "Products",
    "Inventory.totalCategories": "Categories",
    "Inventory.totalSuppliers": "Suppliers",
    "Inventory.totalLocations": "Locations",
    "Inventory.productNameLabel": "Product Name *",
    "Inventory.productNamePlaceholder": "Name the product",
    "Inventory.sku": "SKU",
    "Inventory.skuPlaceholder": "Product code",
    "Inventory.barcode": "Barcode",
    "Inventory.barcodePlaceholder": "Barcode number",
    "Inventory.skus": "SKUs",
    "Inventory.addSku": "Add SKU",
    "Inventory.noSkus": "No SKUs added. Click 'Add SKU' to add one.",
    "Inventory.skuLabelPlaceholder": "Label (optional)",
    "Inventory.currentSku": "Current",
    "Inventory.historicalSku": "Historical",
    "Inventory.scanOrType": "Scan or type manually",
    "Inventory.scan.notFoundTitle": "No product found",
    "Inventory.scan.notFoundDescriptionPrefix": "No product matches barcode ",
    "Inventory.scan.createProduct": "Create product",
    "Inventory.scan.close": "Close",
    "Inventory.scan.finishCurrentMovement": "Finish the current stock movement first.",
    "Inventory.maxSkusReached": "Maximum of 5 SKUs reached",
    "Inventory.description": "Description",
    "Inventory.descriptionPlaceholder": "Product description...",
    "Inventory.selectCategory": "Select category",
    "Inventory.selectSupplier": "Select supplier",
    "Inventory.none": "None",
    "Inventory.costPrice": "Cost Price",
    "Inventory.costPriceZeroWarning": "Without a cost, employee commissions on this product will be calculated on the full retail price.",
    "Inventory.errorDuplicateSku": "You cannot have two products with the same SKU in this salon.",
    "Inventory.retailPrice": "Retail Price",
    "Inventory.initialStock": "Initial Stock",
    "Inventory.lowStockAlert": "Low Stock Alert",
    "Inventory.colLowStockThreshold": "Low threshold",
    "Inventory.otherSkus": "This product has other SKUs. Open the product to manage them.",
    "Inventory.idealStock": "Ideal quantity",
    "Inventory.margin": "Margin",
    "Inventory.idealStockHelp": "The Stock to order report suggests restocking up to this quantity.",
    "Inventory.showingResults": "Showing {start} to {end} of {total} products",
    "Inventory.unit": "Unit",
    "Inventory.unitUnit": "Unit",
    "Inventory.unitBottle": "Bottle",
    "Inventory.unitTube": "Tube",
    "Inventory.unitBox": "Box",
    "Inventory.unitPack": "Pack",
    "Inventory.unitMl": "ML",
    "Inventory.unitOz": "OZ",
    "Inventory.unitG": "Grams",
    "Inventory.cancel": "Cancel",
    "Inventory.create": "Create",
    "Inventory.update": "Update",
    "Inventory.saving": "Saving...",
    "Inventory.fillRequired": "Please fill in all required fields",
    "Inventory.invalidUnitCost": "Unit cost must be a valid positive number",
    "Inventory.nameRequired": "The name is required.",
    "Inventory.invalidPrice": "Prices must be valid positive numbers",
    "Inventory.priceTooLarge": "Prices cannot exceed 99,999,999.99",
    "Inventory.invalidStock": "Stock cannot be negative",
    "Inventory.stockTooLarge": "Stock cannot exceed 2,147,483,647",
    "Inventory.invalidThreshold": "Threshold must be a whole number, zero or more.",
    "Inventory.invalidIdeal": "The ideal quantity must be a whole number, zero or more.",
    "Inventory.idealNotAboveThreshold": "The ideal quantity must be greater than the low stock alert.",
    "Inventory.productsUsingCategory": "products use this category. They will be unlinked.",
    "Inventory.productsUsingSupplier": "products use this supplier. They will be unlinked.",
    "Inventory.productCreated": "Product created",
    "Inventory.productUpdated": "Product updated",
    "Inventory.error": "Error",
    "Inventory.stockMovement": "Stock Movement",
    "Inventory.adjustStock": "Adjust stock levels",
    "Inventory.movementType": "Movement Type",
    "Inventory.purchase": "Purchase",
    "Inventory.sale": "Sale",
    "Inventory.adjustment": "Adjustment",
    "Inventory.waste": "Waste",
    "Inventory.internal_use": "Used by the salon",
    "Inventory.internalUseRemoveStock": "Used by the salon (Remove Stock)",
    "Inventory.adjustmentDirection": "Adjustment direction",
    "Inventory.adjustmentIncrease": "Increase stock",
    "Inventory.adjustmentDecrease": "Decrease stock (broken, expired, lost, miscount)",
    "Inventory.locationStocksUnavailable": "Stock by location unavailable",
    "Inventory.insufficientStockNamed": "Only {available} left at {location}, removing {requested} is not possible.",
    "Inventory.largeQuantityConfirm": "This movement will bring the stock to {result}. Confirm?",
    "Inventory.quantityAboveCap": "A single movement cannot exceed 1,000,000 units.",
    "Inventory.return": "Return to supplier",
    "Inventory.quantityLabel": "Quantity *",
    "Inventory.unitCost": "Unit Cost",
    "Inventory.movementNotes": "Notes",
    "Inventory.movementNotesPlaceholder": "Reason for adjustment...",
    "Inventory.recordMovement": "Record Movement",
    "Inventory.recording": "Recording...",
    "Inventory.selectProduct": "Please select a product",
    "Inventory.enterQuantity": "Please enter a quantity",
    "Inventory.movementRecorded": "Stock movement recorded",
    "Inventory.categories": "Categories",
    "Inventory.manageProductCategories": "Organize products into categories",
    "Inventory.noCategories": "No categories yet",
    "Inventory.createFirst": "Create your first category below",
    "Inventory.categoryName": "Category Name",
    "Inventory.categoryDescription": "Description",
    "Inventory.addCategory": "Add Category",
    "Inventory.adding": "Adding...",
    "Inventory.categoryNameRequired": "Category name is required",
    "Inventory.categoryAdded": "Category added successfully",
    "Inventory.confirmDeleteCategory": "Are you sure you want to delete this category?",
    "Inventory.deleteCategory": "Delete category",
    "Inventory.categoryDeleted": "Category deleted successfully",
    "Inventory.close": "Close",
    "Inventory.suppliers": "Suppliers",
    "Inventory.manageProductSuppliers": "Manage your product suppliers",
    "Inventory.noSuppliers": "No suppliers yet",
    "Inventory.addSupplier": "Add Supplier",
    "Inventory.supplierName": "Supplier Name *",
    "Inventory.contactName": "Contact Name",
    "Inventory.email": "Email",
    "Inventory.phone": "Phone",
    "Inventory.address": "Address",
    "Inventory.supplierNotes": "Notes",
    "Inventory.supplierNameRequired": "Supplier name is required",
    "Inventory.supplierAdded": "Supplier added successfully",
    "Inventory.deleteSupplier": "Delete supplier",
    "Inventory.supplierDeleted": "Supplier deleted successfully",
    "Inventory.useMovement": "Use the stock movement button to adjust inventory",
    "Inventory.success": "Success",
    "Inventory.useStockMovement": "Use the stock movement button to adjust inventory",
    "Inventory.supplierUpdated": "Supplier updated",
    "Inventory.supplierCreated": "Supplier created",
    "Inventory.confirmDeleteSupplier": "Are you sure you want to delete this supplier?",
    "Inventory.manageSuppliersDesc": "Add and manage your product suppliers",
    "Inventory.editSupplier": "Edit Supplier",
    "Inventory.addNewSupplier": "Add New Supplier",
    "Inventory.supplierNamePlaceholder": "Company name",
    "Inventory.contactNamePlaceholder": "Contact name",
    "Inventory.emailPlaceholder": "supplier@example.com",
    "Inventory.phonePlaceholder": "Phone number",
    "Inventory.addressPlaceholder": "Full address",
    "Inventory.supplierNotesPlaceholder": "Additional notes...",
    "Inventory.suppliersList": "Suppliers List",
    "Inventory.name": "Name",
    "Inventory.contact": "Contact",
    "Inventory.quantityMustBePositive": "Quantity must be greater than 0",
    "Inventory.insufficientStock": "Insufficient stock for this operation",
    "Inventory.currentStock": "Current Stock",
    "Inventory.addMovement": "Add Movement",
    "Inventory.purchaseAddStock": "Purchase (Add Stock)",
    "Inventory.saleRemoveStock": "Sale (Remove Stock)",
    "Inventory.returnRemoveStock": "Return to supplier (Remove Stock)",
    "Inventory.wasteRemoveStock": "Waste (Remove Stock)",
    "Inventory.reportReadyForPrinting": "Report ready for printing/download",
    "Inventory.addRemoveStockTooltip": "Add/Remove Stock",
    "Inventory.deleteProductTitle": "Delete product",
    "Inventory.confirmDeleteProduct": "Are you sure you want to delete this product?",
    "Inventory.bulkDelete": "Delete ({count})",
    "Inventory.confirmBulkDeleteTitle": "Delete products",
    "Inventory.confirmBulkDeleteDescription": "Are you sure you want to delete {count} product(s)? This action is irreversible.",
    "Inventory.productDeleted": "Product deleted successfully",
    "Inventory.productsDeleted": "{count} product(s) deleted successfully",
    "Inventory.showArchived": "Show archived products",
    "Inventory.archiveProduct": "Archive",
    "Inventory.restoreProduct": "Restore",
    "Inventory.productArchived": "Product archived",
    "Inventory.productRestored": "Product restored",
    "Inventory.productsArchived": "{count} product(s) archived",
    "Inventory.archivedBadge": "Archived",
    "Inventory.deleteBlockedTitle": "Deletion blocked",
    "Inventory.deleteBlockedMovements": "This product has {count} stock movement(s). Archive it instead.",
    "Inventory.bulkDeleteBlockedMovements": "{blocked} of the {total} selected products have stock movements and cannot be deleted. Archive them instead.",
    "Inventory.unitCostOptional": "Unit Cost (optional)",
    "Inventory.recentMovements": "Recent Movements",
    "Inventory.loadingHistory": "Loading history...",
    "Inventory.noMovements": "No movements recorded yet",
    "Inventory.type": "Type",
    "Inventory.quantity": "Quantity",
    "Inventory.notes": "Notes",
    "Inventory.date": "Date",
    "Inventory.categoryCreated": "Category created successfully",
    "Inventory.categoryUpdated": "Category updated successfully",
    "Inventory.manageCategoriesDescription": "Create and manage product categories",
    "Inventory.editCategory": "Edit Category",
    "Inventory.categoryNamePlaceholder": "Name the product category",
    "Inventory.existingCategories": "Existing Categories",
    "Inventory.movements": "Movements",
    "Inventory.movementsView": "Movements",
    "Inventory.productsView": "Products",
    "Inventory.filterByType": "Type",
    "Inventory.filterByLocation": "Location",
    "Inventory.filterByPeriod": "Period",
    "Inventory.filterByUser": "User",
    "Inventory.searchProduct": "Search a product...",
    "Inventory.allTypes": "All Types",
    "Inventory.allLocations": "All Locations",
    "Inventory.allPeriods": "All Periods",
    "Inventory.allUsers": "All Users",
    "Inventory.today": "Today",
    "Inventory.last7Days": "Last 7 days",
    "Inventory.last30Days": "Last 30 days",
    "Inventory.custom": "Custom",
    "Inventory.product": "Product",
    "Inventory.location": "Location",
    "Inventory.unitCostShort": "Unit Cost",
    "Inventory.noMovementsFound": "No movements found",
    "Inventory.showingMovements": "Showing {start} to {end} of {total} movements",
    "Inventory.clearFilters": "Clear",

    // Product lines (category attached to a supplier)
    "Inventory.lines.supplierOptional": "Supplier (optional)",
    "Inventory.lines.noneGeneral": "None (general category)",
    "Inventory.lines.supplierHint": "Leave empty for a general category shared by all brands.",
    "Inventory.lines.generalGroup": "General categories",
    "Inventory.lines.generalBadge": "General",
    "Inventory.lines.linesOf": "{supplier} lines",
    "Inventory.lines.columnLines": "Lines",
    "Inventory.lines.countOne": "{count} line",
    "Inventory.lines.countMany": "{count} lines",
    "Inventory.lines.productCountOne": "{count} product",
    "Inventory.lines.productCountMany": "{count} products",
    "Inventory.lines.linesTitle": "{supplier} product lines",
    "Inventory.lines.showLines": "Show the product lines of {supplier}",
    "Inventory.lines.noLines": "No lines yet.",
    "Inventory.lines.addPlaceholder": "New line name, e.g. All Soft",
    "Inventory.lines.addButton": "Add line",
    "Inventory.lines.added": "Line \"{name}\" added to {supplier}.",
    "Inventory.lines.lineRemoved": "The category \"{category}\" belongs to {owner}. It was removed: choose a {supplier} line or a general category.",
    "Inventory.lines.lineRemovedNoSupplier": "The category \"{category}\" belongs to {owner}. It was removed: choose a general category, or a line to fill in the supplier.",
    "Inventory.lines.supplierAutoFilled": "Supplier filled in automatically: {supplier}, owner of the line \"{category}\".",
    "Inventory.lines.refuseMixedOne": "Cannot attach \"{category}\" to {supplier} because a product from another supplier is in it: {list}. Change its category first, or keep \"{category}\" as a general category.",
    "Inventory.lines.refuseMixedMany": "Cannot attach \"{category}\" to {supplier} because products from another supplier are in it: {list}. Change their category first, or keep \"{category}\" as a general category.",
    "Inventory.lines.archivedTag": "archived",
    "Inventory.lines.andOneOther": "and 1 more",
    "Inventory.lines.andOthers": "and {count} more",
    "Inventory.lines.confirmInheritTitle": "Attach to {supplier}?",
    "Inventory.lines.confirmInheritOne": "1 product in \"{category}\" has no supplier. It will receive {supplier} as its supplier.",
    "Inventory.lines.confirmInheritMany": "{count} products in \"{category}\" have no supplier. They will receive {supplier} as their supplier.",
    "Inventory.lines.confirmInheritButton": "Attach",
    "Inventory.lines.confirmMoveTitle": "Move this line?",
    "Inventory.lines.confirmMoveOne": "The product in \"{category}\" will move from {from} to {to}.",
    "Inventory.lines.confirmMoveMany": "The {count} products in \"{category}\" will move from {from} to {to}.",
    "Inventory.lines.confirmMoveButton": "Move line",
    "Inventory.lines.commissionWarning": "If you have commissions by supplier, these products will follow the {supplier} rate, including for the current pay period.",
    "Inventory.lines.deleteSupplierLineOne": "Its product line ({names}) will be kept as a general category: your products keep their category.",
    "Inventory.lines.deleteSupplierLinesMany": "Its {count} product lines ({names}) will be kept as general categories: your products keep their category.",
    "Inventory.lines.dbMismatch": "This product's category belongs to another supplier. Choose a line from this supplier or a general category.",
    "Inventory.lines.dbMixed": "This category now contains products from another supplier. Close and reopen the window to see them.",
    "InventoryRemoteChange.stock": "This product's stock just changed on another device.",
    "InventoryRemoteChange.modified": "This product was just modified on another device.",
  },
  fr: {

    // Inventory toast messages (errorToast / successToast wrapper)
    "Inventory.toast.loadCategoriesFailed": "Impossible de charger les catégories",
    "Inventory.toast.loadSuppliersFailed": "Impossible de charger les fournisseurs",
    "Inventory.toast.loadLocationsFailed": "Impossible de charger les emplacements",
    "Inventory.toast.noLocationForStock": "Créez d'abord un emplacement avant d'ajouter du stock initial.",
    "Inventory.toast.loadSkusFailed": "Impossible de charger les SKU",
    "Inventory.toast.loadDataFailed": "Impossible de charger les données d'inventaire",
    "Inventory.toast.loadMovementsFailed": "Impossible de charger l'historique des mouvements",
    "Inventory.toast.invalidPhoto": "Format d'image non supporté",
    "Inventory.toast.saveProductFailed": "Impossible d'enregistrer le produit",
    "Inventory.toast.oldPhotoNotRemoved": "Nouvelle photo enregistrée, mais l'ancien fichier n'a pas pu être supprimé",
    "Inventory.toast.photoNotSaved": "Produit enregistré, mais la photo n'a pas pu être mise à jour",
    "Inventory.toast.saveSupplierFailed": "Impossible d'enregistrer le fournisseur",
    "Inventory.toast.saveCategoryFailed": "Impossible d'enregistrer la catégorie",
    "Inventory.toast.saveLocationFailed": "Impossible d'enregistrer l'emplacement",
    "Inventory.toast.saveMovementFailed": "Impossible d'enregistrer le mouvement",
    "Inventory.toast.deleteProductFailed": "Impossible de supprimer le produit",
    "Inventory.toast.deleteSupplierFailed": "Impossible de supprimer le fournisseur",
    "Inventory.toast.deleteCategoryFailed": "Impossible de supprimer la catégorie",
    "Inventory.toast.deleteLocationFailed": "Impossible de supprimer l'emplacement",
    "Inventory.toast.bulkDeleteFailed": "Impossible de supprimer les produits sélectionnés",
    "Inventory.toast.archiveProductFailed": "Impossible d'archiver le produit",
    "Inventory.toast.restoreProductFailed": "Impossible de restaurer le produit",
    "Inventory.headerSubtitle": "Produits, stock, mouvements et fournisseurs.",
    "Inventory.tabProducts": "Produits",
    "Inventory.tileProducts": "Produits",
    "Inventory.tileLowStock": "Faible stock",
    "Inventory.tileOutOfStock": "Rupture",
    "Inventory.tileStockValue": "Valeur du stock",

    // Inventory
    "Inventory.title": "Inventaire",
    "Inventory.lowStock": "Stock faible",
    "Inventory.outOfStock": "Rupture de stock",
    "Inventory.inStock": "En stock",
    "Inventory.totalValue": "Valeur totale",
    "Inventory.upgradeRequired": "Module d'inventaire non disponible",
    "Inventory.upgradeDescription":
      "Le module d'inventaire n'est pas disponible dans votre plan actuel. Veuillez passer au plan PRO ou ENTREPRISE pour accéder à cette fonctionnalité.",
    "Inventory.contactUs": "Nous contacter pour mettre à niveau",
    "Inventory.noAccessTitle": "Accès refusé",
    "Inventory.noAccessDescription": "Vous n'avez pas la permission de voir l'inventaire. Contactez le propriétaire du salon pour demander l'accès.",
    "Inventory.searchPlaceholder": "Rechercher des produits...",
    "Inventory.filterCategory": "Filtrer par catégorie",
    "Inventory.allCategories": "Toutes les catégories",
    "Inventory.allStockLevels": "Tous les niveaux de stock",
    "Inventory.allLevels": "Tous les niveaux",
    "Inventory.stockLevel": "Niveau de stock",
    "Inventory.newProduct": "Produit",
    "Inventory.newCategory": "Catégorie",
    "Inventory.newSupplier": "Fournisseur",
    "Inventory.newLocation": "Emplacement",
    "Inventory.downloadReport": "Télécharger le rapport",
    "Inventory.reportTitle": "Télécharger le rapport d'inventaire",
    "Inventory.startDate": "Date de début",
    "Inventory.endDate": "Date de fin",
    "Inventory.selectStartDate": "Sélectionner une date",
    "Inventory.selectEndDate": "Sélectionner une date",
    "Inventory.reportDescription":
      "Le rapport inclura tous les mouvements de stock (achats, ventes, retours, pertes et ajustements) pour la période sélectionnée, avec un tableau récapitulatif et une liste détaillée chronologique.",
    "Inventory.reportGenerating": "Génération...",
    "Inventory.downloadPDF": "Télécharger PDF",
    "Inventory.reportGenerated": "Rapport généré",
    "Inventory.reportDownloaded": "Le rapport d'inventaire a été téléchargé avec succès",
    "Inventory.selectPeriod": "Veuillez sélectionner une période",
    "Inventory.startBeforeEnd": "La date de début doit être antérieure à la date de fin",
    "Inventory.errorGeneratingReport": "Erreur lors de la génération du rapport",
    "Inventory.manageCategories": "Gérer les catégories",
    "Inventory.manageSuppliers": "Gérer les fournisseurs",
    "Inventory.productName": "Nom du produit",
    "Inventory.category": "Catégorie",
    "Inventory.supplier": "Fournisseur",
    "Inventory.stock": "Stock",
    "Inventory.cost": "Coût",
    "Inventory.retail": "Détail",
    "Inventory.actions": "Actions",
    "Inventory.filterByCategory": "Filtrer par catégorie",
    "Inventory.filterBySupplier": "Filtrer par fournisseur",
    "Inventory.filterByStatus": "Filtrer par statut",
    "Inventory.loading": "Chargement de l'inventaire...",
    "Inventory.noProducts": "Aucun produit trouvé",
    "Inventory.createProduct": "Créer un produit",
    "Inventory.editProduct": "Modifier le produit",
    "Inventory.addProduct": "Ajouter un nouveau produit à l'inventaire",
    "Inventory.updateProduct": "Mettre à jour les détails du produit",
    "Inventory.productPhoto": "Photo du produit",
    "Inventory.photoHint": "JPEG, PNG ou WebP. Max 800x800px.",
    "Inventory.removePhoto": "Supprimer la photo",
    "Inventory.selected": "sélectionné(s)",
    "Inventory.printLabels": "Imprimer les étiquettes",
    "Inventory.labelPrintTitle": "Impression d'étiquettes produits",
    "Inventory.productsSelected": "produits sélectionnés",
    "Inventory.labelSize": "Format d'étiquette",
    "Inventory.customSize": "Format personnalisé",
    "Inventory.labelWidth": "Largeur (pouces)",
    "Inventory.labelHeight": "Hauteur (pouces)",
    "Inventory.quantityPerProduct": "Qté par produit",
    "Inventory.preview": "Aperçu",
    "Inventory.print": "Imprimer",
    "Inventory.locations": "Emplacements",
    "Inventory.manageLocations": "Gérer les emplacements",
    "Inventory.manageLocationsDescription": "Créer et gérer les emplacements de stockage de votre salon",
    "Inventory.locationName": "Nom de l'emplacement",
    "Inventory.locationNamePlaceholder": "ex: Vitrine, Backstore",
    "Inventory.locationColor": "Couleur",
    "Inventory.addLocation": "Ajouter un emplacement",
    "Inventory.editLocation": "Modifier l'emplacement",
    "Inventory.locationCreated": "Emplacement créé",
    "Inventory.locationUpdated": "Emplacement mis à jour",
    "Inventory.confirmDeleteLocation": "Êtes-vous sûr de vouloir supprimer cet emplacement ?",
    "Inventory.deleteLocation": "Supprimer l'emplacement",
    "Inventory.locationDeleted": "Emplacement supprimé",
    "Inventory.existingLocations": "Emplacements existants",
    "Inventory.noLocations": "Aucun emplacement",
    "Inventory.locationNameRequired": "Le nom de l'emplacement est requis",
    "Inventory.maxLocationsReached": "Maximum 5 emplacements par salon",
    "Inventory.cannotDeleteLastLocation": "Impossible de supprimer le dernier emplacement",
    "Inventory.cannotDeleteLocationWithStock": "Impossible de supprimer un emplacement avec du stock. Transférez le stock d'abord.",
    "Inventory.cannotVerifyLocationStock": "Le stock de cet emplacement n'a pas pu être vérifié. Fermez cette fenêtre et réessayez.",
    "Inventory.transfer": "Transfert",
    "Inventory.transferStock": "Transfert entre emplacements",
    "Inventory.sourceLocation": "Emplacement source",
    "Inventory.destinationLocation": "Emplacement de destination",
    "Inventory.selectLocation": "Sélectionner un emplacement",
    "Inventory.deductFromLocation": "Déduire de",
    "Inventory.stockByLocation": "Stock par emplacement",
    "Inventory.locationRequired": "Veuillez sélectionner un emplacement",
    "Inventory.destinationRequired": "Veuillez sélectionner un emplacement de destination",
    "Inventory.cannotTransferSameLocation": "La source et la destination doivent être différentes",
    "Inventory.insufficientLocationStock": "Stock insuffisant à cet emplacement",
    "Inventory.initialStockLocation": "Emplacement du stock initial",
    "Inventory.totalProducts": "Produits",
    "Inventory.totalCategories": "Catégories",
    "Inventory.totalSuppliers": "Fournisseurs",
    "Inventory.totalLocations": "Emplacements",
    "Inventory.productNameLabel": "Nom du produit *",
    "Inventory.productNamePlaceholder": "Nommez le produit",
    "Inventory.sku": "SKU",
    "Inventory.skuPlaceholder": "Code produit",
    "Inventory.barcode": "Code-barres",
    "Inventory.barcodePlaceholder": "Numéro de code-barres",
    "Inventory.skus": "SKUs",
    "Inventory.addSku": "Ajouter un SKU",
    "Inventory.noSkus": "Aucun SKU ajouté. Cliquez sur 'Ajouter un SKU' pour en ajouter un.",
    "Inventory.skuLabelPlaceholder": "Libellé (optionnel)",
    "Inventory.currentSku": "Actuel",
    "Inventory.historicalSku": "Historique",
    "Inventory.scanOrType": "Scannez ou saisissez manuellement",
    "Inventory.scan.notFoundTitle": "Aucun produit trouvé",
    "Inventory.scan.notFoundDescriptionPrefix": "Aucun produit ne correspond au code-barres ",
    "Inventory.scan.createProduct": "Créer le produit",
    "Inventory.scan.close": "Fermer",
    "Inventory.scan.finishCurrentMovement": "Terminez d'abord le mouvement de stock en cours.",
    "Inventory.maxSkusReached": "Maximum de 5 SKUs atteint",
    "Inventory.description": "Description",
    "Inventory.descriptionPlaceholder": "Description du produit...",
    "Inventory.selectCategory": "Sélectionner une catégorie",
    "Inventory.selectSupplier": "Sélectionner un fournisseur",
    "Inventory.none": "Aucun",
    "Inventory.costPrice": "Prix coûtant",
    "Inventory.costPriceZeroWarning": "Sans coût, les commissions des employés sur ce produit seront calculées sur le prix de vente complet.",
    "Inventory.errorDuplicateSku": "Vous ne pouvez pas avoir deux produits avec le même SKU dans ce salon.",
    "Inventory.retailPrice": "Prix de détail",
    "Inventory.initialStock": "Stock initial",
    "Inventory.lowStockAlert": "Alerte stock faible",
    "Inventory.colLowStockThreshold": "Seuil bas",
    "Inventory.otherSkus": "Ce produit a d'autres SKU. Ouvrez la fiche pour les gérer.",
    "Inventory.idealStock": "Quantité idéale",
    "Inventory.margin": "Marge",
    "Inventory.idealStockHelp": "Le rapport Stock à commander suggère de remonter jusqu'à cette quantité.",
    "Inventory.showingResults": "Affichage de {start} à {end} sur {total} produits",
    "Inventory.unit": "Unité",
    "Inventory.unitUnit": "Unité",
    "Inventory.unitBottle": "Bouteille",
    "Inventory.unitTube": "Tube",
    "Inventory.unitBox": "Boîte",
    "Inventory.unitPack": "Paquet",
    "Inventory.unitMl": "ML",
    "Inventory.unitOz": "OZ",
    "Inventory.unitG": "Grammes",
    "Inventory.cancel": "Annuler",
    "Inventory.create": "Créer",
    "Inventory.update": "Mettre à jour",
    "Inventory.saving": "Enregistrement...",
    "Inventory.fillRequired": "Veuillez remplir tous les champs obligatoires",
    "Inventory.invalidUnitCost": "Le coût unitaire doit être un nombre positif valide",
    "Inventory.nameRequired": "Le nom est obligatoire.",
    "Inventory.invalidPrice": "Les prix doivent être des nombres positifs valides",
    "Inventory.priceTooLarge": "Les prix ne peuvent pas dépasser 99 999 999,99",
    "Inventory.invalidStock": "Le stock ne peut pas être négatif",
    "Inventory.stockTooLarge": "Le stock ne peut pas dépasser 2 147 483 647",
    "Inventory.invalidThreshold": "Le seuil doit être un nombre entier positif ou nul.",
    "Inventory.invalidIdeal": "La quantité idéale doit être un nombre entier positif ou nul.",
    "Inventory.idealNotAboveThreshold": "La quantité idéale doit être plus grande que l'alerte stock faible.",
    "Inventory.productsUsingCategory": "produit(s) utilisent cette catégorie. Ils seront dissociés.",
    "Inventory.productsUsingSupplier": "produit(s) utilisent ce fournisseur. Ils seront dissociés.",
    "Inventory.productCreated": "Produit créé",
    "Inventory.productUpdated": "Produit mis à jour",
    "Inventory.error": "Erreur",
    "Inventory.stockMovement": "Mouvement de stock",
    "Inventory.adjustStock": "Ajuster les niveaux de stock",
    "Inventory.movementType": "Type de mouvement",
    "Inventory.purchase": "Achat",
    "Inventory.sale": "Vente",
    "Inventory.adjustment": "Ajustement",
    "Inventory.waste": "Perte",
    "Inventory.internal_use": "Utilisé par le salon",
    "Inventory.internalUseRemoveStock": "Utilisé par le salon (retirer du stock)",
    "Inventory.adjustmentDirection": "Sens de l'ajustement",
    "Inventory.adjustmentIncrease": "Augmenter le stock",
    "Inventory.adjustmentDecrease": "Diminuer le stock (cassé, périmé, perdu, erreur de comptage)",
    "Inventory.locationStocksUnavailable": "Stock par emplacement indisponible",
    "Inventory.insufficientStockNamed": "Il ne reste que {available} unités à {location}, un retrait de {requested} est impossible.",
    "Inventory.largeQuantityConfirm": "Ce mouvement portera le stock à {result}. Confirmer ?",
    "Inventory.quantityAboveCap": "Un seul mouvement ne peut pas dépasser 1 000 000 unités.",
    "Inventory.return": "Retour au fournisseur",
    "Inventory.quantityLabel": "Quantité *",
    "Inventory.unitCost": "Coût unitaire",
    "Inventory.movementNotes": "Notes",
    "Inventory.movementNotesPlaceholder": "Raison de l'ajustement...",
    "Inventory.recordMovement": "Enregistrer le mouvement",
    "Inventory.recording": "Enregistrement...",
    "Inventory.selectProduct": "Veuillez sélectionner un produit",
    "Inventory.enterQuantity": "Veuillez entrer une quantité",
    "Inventory.movementRecorded": "Mouvement de stock enregistré",
    "Inventory.categories": "Catégories",
    "Inventory.manageProductCategories": "Organiser les produits en catégories",
    "Inventory.noCategories": "Aucune catégorie encore",
    "Inventory.createFirst": "Créez votre première catégorie ci-dessous",
    "Inventory.categoryName": "Nom de la catégorie",
    "Inventory.categoryDescription": "Description",
    "Inventory.addCategory": "Ajouter une catégorie",
    "Inventory.adding": "Ajout...",
    "Inventory.categoryNameRequired": "Le nom de la catégorie est obligatoire",
    "Inventory.categoryAdded": "Catégorie ajoutée avec succès",
    "Inventory.confirmDeleteCategory": "Êtes-vous sûr de vouloir supprimer cette catégorie ?",
    "Inventory.deleteCategory": "Supprimer la catégorie",
    "Inventory.categoryDeleted": "Catégorie supprimée avec succès",
    "Inventory.close": "Fermer",
    "Inventory.suppliers": "Fournisseurs",
    "Inventory.manageProductSuppliers": "Gérer vos fournisseurs de produits",
    "Inventory.noSuppliers": "Aucun fournisseur encore",
    "Inventory.addSupplier": "Ajouter un fournisseur",
    "Inventory.supplierName": "Nom du fournisseur *",
    "Inventory.contactName": "Nom du contact",
    "Inventory.email": "Email",
    "Inventory.phone": "Téléphone",
    "Inventory.address": "Adresse",
    "Inventory.supplierNotes": "Notes",
    "Inventory.supplierNameRequired": "Le nom du fournisseur est obligatoire",
    "Inventory.supplierAdded": "Fournisseur ajouté avec succès",
    "Inventory.deleteSupplier": "Supprimer le fournisseur",
    "Inventory.supplierDeleted": "Fournisseur supprimé avec succès",
    "Inventory.useMovement": "Utilisez le bouton de mouvement de stock pour ajuster l'inventaire",
    "Inventory.success": "Succès",
    "Inventory.useStockMovement": "Utilisez le bouton de mouvement de stock pour ajuster l'inventaire",
    "Inventory.supplierUpdated": "Fournisseur mis à jour",
    "Inventory.supplierCreated": "Fournisseur créé",
    "Inventory.confirmDeleteSupplier": "Êtes-vous sûr de vouloir supprimer ce fournisseur ?",
    "Inventory.manageSuppliersDesc": "Ajoutez et gérez vos fournisseurs de produits",
    "Inventory.editSupplier": "Modifier le fournisseur",
    "Inventory.addNewSupplier": "Ajouter un nouveau fournisseur",
    "Inventory.supplierNamePlaceholder": "Nom de l'entreprise",
    "Inventory.contactNamePlaceholder": "Nom du contact",
    "Inventory.emailPlaceholder": "fournisseur@exemple.com",
    "Inventory.phonePlaceholder": "Numéro de téléphone",
    "Inventory.addressPlaceholder": "Adresse complète",
    "Inventory.supplierNotesPlaceholder": "Notes supplémentaires...",
    "Inventory.suppliersList": "Liste des fournisseurs",
    "Inventory.name": "Nom",
    "Inventory.contact": "Contact",
    "Inventory.quantityMustBePositive": "La quantité doit être supérieure à 0",
    "Inventory.insufficientStock": "Stock insuffisant pour cette opération",
    "Inventory.currentStock": "Stock actuel",
    "Inventory.addMovement": "Ajouter un mouvement",
    "Inventory.purchaseAddStock": "Achat (ajouter du stock)",
    "Inventory.saleRemoveStock": "Vente (retirer du stock)",
    "Inventory.returnRemoveStock": "Retour au fournisseur (retirer du stock)",
    "Inventory.wasteRemoveStock": "Perte (retirer du stock)",
    "Inventory.reportReadyForPrinting": "Rapport prêt pour l'impression/téléchargement",
    "Inventory.addRemoveStockTooltip": "Ajouter/retirer du stock",
    "Inventory.deleteProductTitle": "Supprimer le produit",
    "Inventory.confirmDeleteProduct": "Êtes-vous sûr de vouloir supprimer ce produit?",
    "Inventory.bulkDelete": "Supprimer ({count})",
    "Inventory.confirmBulkDeleteTitle": "Supprimer les produits",
    "Inventory.confirmBulkDeleteDescription": "Êtes-vous sûr de vouloir supprimer {count} produit(s) ? Cette action est irréversible.",
    "Inventory.productDeleted": "Produit supprimé avec succès",
    "Inventory.productsDeleted": "{count} produit(s) supprimé(s) avec succès",
    "Inventory.showArchived": "Afficher les produits archivés",
    "Inventory.archiveProduct": "Archiver",
    "Inventory.restoreProduct": "Restaurer",
    "Inventory.productArchived": "Produit archivé",
    "Inventory.productRestored": "Produit restauré",
    "Inventory.productsArchived": "{count} produit(s) archivé(s)",
    "Inventory.archivedBadge": "Archivé",
    "Inventory.deleteBlockedTitle": "Suppression bloquée",
    "Inventory.deleteBlockedMovements": "Ce produit a {count} mouvement(s) de stock. Archivez-le plutôt.",
    "Inventory.bulkDeleteBlockedMovements": "{blocked} des {total} produits sélectionnés ont des mouvements de stock et ne peuvent pas être supprimés. Archivez-les plutôt.",
    "Inventory.unitCostOptional": "Coût unitaire (optionnel)",
    "Inventory.recentMovements": "Mouvements récents",
    "Inventory.loadingHistory": "Chargement de l'historique...",
    "Inventory.noMovements": "Aucun mouvement enregistré",
    "Inventory.type": "Type",
    "Inventory.quantity": "Quantité",
    "Inventory.notes": "Notes",
    "Inventory.date": "Date",
    "Inventory.categoryCreated": "Catégorie créée avec succès",
    "Inventory.categoryUpdated": "Catégorie mise à jour avec succès",
    "Inventory.manageCategoriesDescription": "Créer et gérer les catégories de produits",
    "Inventory.editCategory": "Modifier la catégorie",
    "Inventory.categoryNamePlaceholder": "Nommez la catégorie de produits",
    "Inventory.existingCategories": "Catégories existantes",
    "Inventory.movements": "Mouvements",
    "Inventory.movementsView": "Mouvements",
    "Inventory.productsView": "Produits",
    "Inventory.filterByType": "Type",
    "Inventory.filterByLocation": "Emplacement",
    "Inventory.filterByPeriod": "Période",
    "Inventory.filterByUser": "Utilisateur",
    "Inventory.searchProduct": "Rechercher un produit...",
    "Inventory.allTypes": "Tous les types",
    "Inventory.allLocations": "Tous les emplacements",
    "Inventory.allPeriods": "Toutes les périodes",
    "Inventory.allUsers": "Tous les utilisateurs",
    "Inventory.today": "Aujourd'hui",
    "Inventory.last7Days": "7 derniers jours",
    "Inventory.last30Days": "30 derniers jours",
    "Inventory.custom": "Personnalisé",
    "Inventory.product": "Produit",
    "Inventory.location": "Emplacement",
    "Inventory.unitCostShort": "Coût unit.",
    "Inventory.noMovementsFound": "Aucun mouvement trouvé",
    "Inventory.showingMovements": "Affichage de {start} à {end} sur {total} mouvements",
    "Inventory.clearFilters": "Effacer",

    // Lignes de produits (catégorie rattachée à un fournisseur)
    "Inventory.lines.supplierOptional": "Fournisseur (facultatif)",
    "Inventory.lines.noneGeneral": "Aucun (catégorie générale)",
    "Inventory.lines.supplierHint": "Laissez vide pour une catégorie générale, commune à toutes les marques.",
    "Inventory.lines.generalGroup": "Catégories générales",
    "Inventory.lines.generalBadge": "Générale",
    "Inventory.lines.linesOf": "Lignes {supplier}",
    "Inventory.lines.columnLines": "Lignes",
    "Inventory.lines.countOne": "{count} ligne",
    "Inventory.lines.countMany": "{count} lignes",
    "Inventory.lines.productCountOne": "{count} produit",
    "Inventory.lines.productCountMany": "{count} produits",
    "Inventory.lines.linesTitle": "Lignes de produits {supplier}",
    "Inventory.lines.showLines": "Afficher les lignes de produits de {supplier}",
    "Inventory.lines.noLines": "Aucune ligne pour l'instant.",
    "Inventory.lines.addPlaceholder": "Nom de la nouvelle ligne, ex. : All Soft",
    "Inventory.lines.addButton": "Ajouter la ligne",
    "Inventory.lines.added": "Ligne « {name} » ajoutée à {supplier}.",
    "Inventory.lines.lineRemoved": "La catégorie « {category} » appartient à {owner}. Elle a été retirée : choisissez une ligne de {supplier} ou une catégorie générale.",
    "Inventory.lines.lineRemovedNoSupplier": "La catégorie « {category} » appartient à {owner}. Elle a été retirée : choisissez une catégorie générale, ou une ligne pour remplir le fournisseur.",
    "Inventory.lines.supplierAutoFilled": "Fournisseur rempli automatiquement : {supplier}, propriétaire de la ligne « {category} ».",
    "Inventory.lines.refuseMixedOne": "Impossible de rattacher « {category} » à {supplier}, car un produit d'un autre fournisseur s'y trouve : {list}. Changez d'abord sa catégorie, ou laissez « {category} » en catégorie générale.",
    "Inventory.lines.refuseMixedMany": "Impossible de rattacher « {category} » à {supplier}, car des produits d'un autre fournisseur s'y trouvent : {list}. Changez d'abord leur catégorie, ou laissez « {category} » en catégorie générale.",
    "Inventory.lines.archivedTag": "archivé",
    "Inventory.lines.andOneOther": "et 1 autre",
    "Inventory.lines.andOthers": "et {count} autres",
    "Inventory.lines.confirmInheritTitle": "Rattacher à {supplier} ?",
    "Inventory.lines.confirmInheritOne": "1 produit de « {category} » n'a pas de fournisseur. Il recevra {supplier} comme fournisseur.",
    "Inventory.lines.confirmInheritMany": "{count} produits de « {category} » n'ont pas de fournisseur. Ils recevront {supplier} comme fournisseur.",
    "Inventory.lines.confirmInheritButton": "Rattacher",
    "Inventory.lines.confirmMoveTitle": "Déplacer cette ligne ?",
    "Inventory.lines.confirmMoveOne": "Le produit de « {category} » passera de {from} à {to}.",
    "Inventory.lines.confirmMoveMany": "Les {count} produits de « {category} » passeront de {from} à {to}.",
    "Inventory.lines.confirmMoveButton": "Déplacer la ligne",
    "Inventory.lines.commissionWarning": "Si vous avez des commissions par fournisseur, ces produits suivront le taux de {supplier}, y compris pour la paie en cours.",
    "Inventory.lines.deleteSupplierLineOne": "Sa ligne de produits ({names}) est conservée et devient une catégorie générale : vos produits gardent leur catégorie.",
    "Inventory.lines.deleteSupplierLinesMany": "Ses {count} lignes de produits ({names}) sont conservées et deviennent des catégories générales : vos produits gardent leur catégorie.",
    "Inventory.lines.dbMismatch": "La catégorie de ce produit appartient à un autre fournisseur. Choisissez une ligne de ce fournisseur ou une catégorie générale.",
    "Inventory.lines.dbMixed": "Cette catégorie contient maintenant des produits d'un autre fournisseur. Fermez puis rouvrez la fenêtre pour les voir.",
    "InventoryRemoteChange.stock": "Le stock de ce produit vient de changer sur un autre appareil.",
    "InventoryRemoteChange.modified": "Ce produit vient d'être modifié sur un autre appareil.",
  },
};
